import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { Form, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { Link, Redirect } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet-async';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage><IonContent>
    <main className="container py-4" style={{ maxWidth: 440, margin: '0 auto' }}>
      <Helmet>
        <title>Sign In | QA Master</title>
        <meta name="description" content="Sign in to QA Master to create, run and review your AI-generated automated tests." />
        <link rel="canonical" href="https://qamaster.uk/login" />
      </Helmet>
      <div>
              <div style={{ textAlign: 'center', marginTop: 60, marginBottom: 40 }}>
                <h1 style={{
                  fontSize: 32, fontWeight: 700, margin: 0,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                }}>QA Master</h1>
                <p style={{ color: '#666', marginTop: 8 }}>Sign in to your account</p>
              </div>

        <div className="bg-white rounded-3 shadow-sm p-4">
          <form onSubmit={handleSubmit}>
            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fw-semibold small">Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <div className="text-end mb-3">
              <Link to="/forgot-password" className="small text-decoration-none">Forgot password?</Link>
            </div>

            <Button
              type="submit"
              className="w-100 btn-brand-gradient fw-semibold"
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : 'Sign In'}
            </Button>
          </form>
        </div>

        <p className="text-center text-muted mt-4 small">
          Don't have an account?{' '}
          <Link to="/register" className="fw-semibold text-decoration-none">Create one</Link>
        </p>
        <p className="text-center mt-2">
          <Link to="/" className="text-secondary small text-decoration-none">Back to home</Link>
        </p>
      </div>
    </main>
    </IonContent></IonPage>
  );
};

export default Login;
